import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSession } from '../model/selectors';
import { SessionType, isRunningLocal, builtEnvironment } from '@shared';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';

const MAX_RETRIES = 4;
const RETRY_BASE_DELAY_MS = 1000;
const FETCH_TIMEOUT_MS = 16000;

function buildClientSignInFallbackUrl(formReferenceId: string, sourceQuery: string): string {
  const redirectTo = `/onboarding-form/${formReferenceId}${sourceQuery}`;
  const params = new URLSearchParams({
    client_sign_in: 'true',
    redirect_to: redirectTo,
  });
  return `/?${params.toString()}`;
}

function jitteredDelay(attempt: number): Promise<void> {
  const cap = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
  const delay = Math.random() * cap;
  return new Promise(r => setTimeout(r, delay));
}

// A dropped connection can surface as a rejected fetch while the browser keeps
// reusing the same pooled socket on retry. A unique param per attempt plus
// no-store makes each retry a distinct, uncached request.
function withCacheBuster(url: string, attempt: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_retry=${attempt}-${Date.now()}`;
}

async function fetchWithRetry(url: string, retries: number): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(withCacheBuster(url, attempt), {
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
        cache: 'no-store',
      });
      if (response.ok) {
        return response;
      }
      if (attempt < retries && response.status >= 500) {
        await jitteredDelay(attempt);
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < retries) {
        await jitteredDelay(attempt);
        continue;
      }
      throw error;
    }
  }
  throw new Error('Exhausted retries');
}

export function OnboardingRedirect() {
  const { formReferenceId } = useParams<{ formReferenceId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preserve the channel tag through every onward hop so the open-time resolver can record it.
  const source = searchParams.get('source');
  const sourceQuery = source ? `?source=${encodeURIComponent(source)}` : '';
  const { isClerkSignedIn: isAuthenticated, isClerkLoading: authLoading } = useClerkClientAuth();
  const session = useSelector(selectSession);
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (!formReferenceId || hasRedirected) {
      return;
    }

    if (authLoading) {
      return;
    }

    if (isAuthenticated && session.sessionType === SessionType.unknown) {
      return;
    }

    if (isAuthenticated && (session.sessionType === SessionType.client || session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard)) {
      setHasRedirected(true);
      navigate(`/onboarding-form/${formReferenceId}${sourceQuery}`, { replace: true });
      return;
    }

    if (!isAuthenticated) {
      setHasRedirected(true);
      
      async function handleRedirect() {
        try {
          const env = isRunningLocal() ? 'dev' : builtEnvironment;
          const response = await fetchWithRetry(`/${env}/api/onboarding/${formReferenceId}${sourceQuery}`, MAX_RETRIES);

          if (!response.ok) {
            console.warn('Onboarding redirect API returned non-OK status, falling back to client sign-in flow');
            navigate(buildClientSignInFallbackUrl(formReferenceId!, sourceQuery), { replace: true });
            return;
          }

          const data = await response.json();
          if (data.localStorageAllowed === true && data.surveyUrl) {
            window.location.href = data.surveyUrl;
          } else {
            window.location.href = data.redirectUrl;
          }
        } catch (error) {
          console.error('Error fetching onboarding redirect after retries:', error);
          navigate(buildClientSignInFallbackUrl(formReferenceId!, sourceQuery), { replace: true });
        }
      }

      handleRedirect();
    }
  }, [formReferenceId, sourceQuery, navigate, isAuthenticated, authLoading, session.sessionType, hasRedirected]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    </div>
  );
}

